import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { useDateFnsLocale } from '@/i18n/dateLocale'

// Rótulo de mês que vira um SELETOR: clicar abre um grid de 12 meses com navegação
// de ano, para pular direto (ex.: julho → dezembro) sem clicar mês a mês nas setas.
// Substitui apenas o texto do mês nos navegadores já existentes das páginas.
// O menu é portalado com position: fixed para nunca ser cortado pelo container.

export function MonthPicker({
  value,
  onChange,
  className,
}: {
  value: Date
  onChange: (d: Date) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  // Ano em exibição no popover (pode diferir do ano selecionado ao navegar).
  const [viewYear, setViewYear] = useState(value.getFullYear())
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const now = new Date()
  const locale = useDateFnsLocale()
  // "junho 2026" → "Junho 2026" (capitaliza só a 1ª letra). Locale conforme o idioma.
  const label = format(value, 'MMMM yyyy', { locale })
  const labelCap = label.charAt(0).toUpperCase() + label.slice(1)
  // Abreviações dos 12 meses no idioma atual (grid do seletor).
  const MESES = useMemo(
    () => Array.from({ length: 12 }, (_, i) => format(new Date(2000, i, 1), 'MMM', { locale })),
    [locale],
  )

  // Ao abrir, sincroniza o ano exibido com o mês selecionado e posiciona o menu.
  useLayoutEffect(() => {
    if (!open) return
    setViewYear(value.getFullYear())
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const width = 260
    const left = Math.min(
      Math.max(8, r.left + r.width / 2 - width / 2),
      window.innerWidth - width - 8,
    )
    setPos({ left, top: r.bottom + 6, width })
  }, [open, value])

  // Fecha ao clicar fora, rolar ou apertar Esc.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  function pick(monthIdx: number) {
    onChange(new Date(viewYear, monthIdx, 1))
    setOpen(false)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex items-center gap-1 rounded font-medium outline-none hover:text-primary focus-visible:ring-ring/50 focus-visible:ring-2',
          className,
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {labelCap}
        <ChevronsUpDown className="size-3.5 opacity-50" />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width }}
            className="bg-popover text-popover-foreground z-[60] rounded-md border p-2 shadow-md"
          >
            {/* Navegação de ANO */}
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewYear((y) => y - 1)}
                className="hover:bg-accent rounded p-1"
                aria-label="Ano anterior"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="text-sm font-semibold tabular-nums">{viewYear}</span>
              <button
                type="button"
                onClick={() => setViewYear((y) => y + 1)}
                className="hover:bg-accent rounded p-1"
                aria-label="Próximo ano"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
            {/* Grid de 12 meses */}
            <div className="grid grid-cols-3 gap-1">
              {MESES.map((m, i) => {
                const selected = value.getFullYear() === viewYear && value.getMonth() === i
                const isNow = now.getFullYear() === viewYear && now.getMonth() === i
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => pick(i)}
                    className={cn(
                      'rounded px-2 py-1.5 text-sm capitalize transition-colors',
                      selected
                        ? 'bg-primary text-primary-foreground'
                        : isNow
                          ? 'border-primary text-primary border'
                          : 'hover:bg-accent',
                    )}
                  >
                    {m}
                  </button>
                )
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
